import React from 'react'

import ContentSection from '@/app/components/PageBuilder/ContentSection'
import Cta from '@/app/components/PageBuilder/CtaSection'
import DownloadArea from '@/app/components/PageBuilder/DownloadArea'
import FAQArea from '@/app/components/PageBuilder/FAQArea'
import FaqTeaser from '@/app/components/PageBuilder/FaqTeaser'
import GuideArea from '@/app/components/PageBuilder/GuideArea'
import GuideTeaser from '@/app/components/PageBuilder/GuideTeaser'
import Hero from '@/app/components/PageBuilder/HeroSection'
import SharedSection from '@/app/components/PageBuilder/SharedSection'
import SideBySide from '@/app/components/PageBuilder/SideBySide'
import ThreeSteps from '@/app/components/PageBuilder/ThreeSteps'
import { Language } from '@/lang'
import { AllGuidesQueryResult, FaqQueryResult } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

type BlocksType = {
  [key: string]: React.FC<any>
}

type BlockType = {
  _type: string
  _key: string
}

type BlockProps = {
  index: number
  block: BlockType
  pageId: string
  pageType: string
  faqData?: FaqQueryResult | null
  guideData?: AllGuidesQueryResult | null
  language?: Language
}

const Blocks: BlocksType = {
  contentSection: ContentSection,
  ctaSection: Cta,
  downloadArea: DownloadArea,
  faqArea: FAQArea,
  faqTeaser: FaqTeaser,
  guideArea: GuideArea,
  guideTeaser: GuideTeaser,
  heroSection: Hero,
  sharedSectionReference: SharedSection,
  sideBySide: SideBySide,
  threeSteps: ThreeSteps,
}

/**
 * Used by the <PageBuilder>, this component renders a the component that matches the block type.
 */
export default function BlockRenderer({
  block,
  index,
  pageId,
  pageType,
  faqData,
  guideData,
  language,
}: BlockProps) {
  // Block does exist
  if (typeof Blocks[block._type] !== 'undefined') {
    // Prepare props for the component
    const componentProps: any = {
      key: block._key,
      block: block,
      index: index,
      pageId: pageId,
      pageType: pageType,
      language,
    }

    // Pass faqData only to FAQArea component
    if (block._type === 'faqArea') {
      componentProps.faqData = faqData
    }

    // Pass guideData only to GuideArea component
    if (block._type === 'guideArea') {
      componentProps.guideData = guideData
    }

    return (
      <div
        key={block._key}
        data-sanity={dataAttr({
          id: pageId,
          type: pageType,
          path: `pageBuilder[_key=="${block._key}"]`,
        }).toString()}
      >
        {React.createElement(Blocks[block._type], componentProps)}
      </div>
    )
  }
  // Block doesn't exist yet
  return React.createElement(
    () => (
      <div className="w-full bg-gray-100 text-center text-gray-500 p-20 rounded">
        A &ldquo;{block._type}&rdquo; block hasn&apos;t been created
      </div>
    ),
    { key: block._key },
  )
}
